import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import { Save as SaveIcon, Edit as EditIcon } from '@mui/icons-material';
import { StoreGeneralSettings } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';

interface GeneralSettingsProps {
  settings: StoreGeneralSettings;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<StoreGeneralSettings>(settings);
  const [editing, setEditing] = useState(false);

  // طباعة حالة التحرير عند تغييرها
  React.useEffect(() => {
    console.log('Editing state changed:', editing);
  }, [editing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;

    if (name === 'isStoreEnabled') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name.startsWith('socialLinks.')) {
      const socialKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [socialKey]: value
        }
      }));
    } else if (name.startsWith('workingHours.')) {
      const dayKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        workingHours: {
          ...prev.workingHours,
          [dayKey]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('general', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ الإعدادات العامة');
    } finally {
      onSave(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          الإعدادات العامة للمتجر
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => {
              console.log('Edit button clicked, setting editing to true');
              setEditing(true);
            }}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      {/* زر إضافي لتفعيل التحرير مباشرة */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => {
            console.log('Force edit button clicked');
            setEditing(true);
          }}
        >
          انقر هنا لتفعيل التحرير مباشرة
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isStoreEnabled}
                onChange={handleChange}
                name="isStoreEnabled"
                disabled={!editing}
              />
            }
            label="تفعيل المتجر الإلكتروني"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="اسم المتجر"
            name="storeName"
            value={formData.storeName}
            onChange={handleChange}
            disabled={!editing}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="البريد الإلكتروني"
            name="storeEmail"
            type="email"
            value={formData.storeEmail}
            onChange={handleChange}
            disabled={!editing}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="رقم الهاتف"
            name="storePhone"
            value={formData.storePhone}
            onChange={handleChange}
            disabled={!editing}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="عنوان المتجر"
            name="storeAddress"
            value={formData.storeAddress}
            onChange={handleChange}
            disabled={!editing}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="وصف المتجر"
            name="storeDescription"
            value={formData.storeDescription}
            onChange={handleChange}
            disabled={!editing}
            multiline
            rows={3}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            روابط التواصل الاجتماعي
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="فيسبوك"
                  name="socialLinks.facebook"
                  value={formData.socialLinks.facebook || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="تويتر"
                  name="socialLinks.twitter"
                  value={formData.socialLinks.twitter || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="انستغرام"
                  name="socialLinks.instagram"
                  value={formData.socialLinks.instagram || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="يوتيوب"
                  name="socialLinks.youtube"
                  value={formData.socialLinks.youtube || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="واتساب"
                  name="socialLinks.whatsapp"
                  value={formData.socialLinks.whatsapp || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            ساعات العمل
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الأحد"
                  name="workingHours.sunday"
                  value={formData.workingHours.sunday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الاثنين"
                  name="workingHours.monday"
                  value={formData.workingHours.monday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الثلاثاء"
                  name="workingHours.tuesday"
                  value={formData.workingHours.tuesday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الأربعاء"
                  name="workingHours.wednesday"
                  value={formData.workingHours.wednesday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الخميس"
                  name="workingHours.thursday"
                  value={formData.workingHours.thursday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الجمعة"
                  name="workingHours.friday"
                  value={formData.workingHours.friday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="السبت"
                  name="workingHours.saturday"
                  value={formData.workingHours.saturday || ''}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setFormData(settings);
              setEditing(false);
            }}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default GeneralSettings;
